import Computer from '@mui/icons-material/Computer';
import { MobileIcon } from '@openx/components-core/icons/MobileIcon/MobileIcon';
import { TVDevicesIcon } from '@openx/components-core/icons/TVDevicesIcon/TVDevicesIcon';
import { PackageFields } from '@openx/types/package';
import { TargetingFields } from '@openx/types/targeting/targeting';
import { noop } from '@openx/utils/noop';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type Section, TargetingElementKeys, type TargetingElementKeysType } from '../../context/types';
import { convertApiToSet } from '../../utils/convertApiToSet';
import { useTargetingSelectionActions } from '../useTargetingSelectionActions';

export const useDeviceInitialState = (): Section => {
  const { t } = useTranslation();
  const { toggleSubElementSelection, toggleElementSelection } = useTargetingSelectionActions();
  const { getValues } = useFormContext() || { getValues: noop };

  return useMemo(() => {
    const initFormat = getValues(`${PackageFields.TARGETING}.${TargetingFields.INVENTORY_FORMAT}.ad_placement.val`);
    const formValue = getValues(`${PackageFields.TARGETING}.${TargetingFields.INVENTORY_FORMAT}.device_type`);
    const initValues = convertApiToSet([formValue?.desktop_devices, formValue?.mobile_devices, formValue?.tv_devices]);

    const isSelected = (key: TargetingElementKeysType): boolean => initValues.has(key);

    const isGenericDeviceDisabled = initFormat === TargetingElementKeys.FORMAT_CTV ? true : !initFormat;
    const isTvDevicesDisabled =
      initFormat === TargetingElementKeys.FORMAT_BANNER ||
      initFormat === TargetingElementKeys.FORMAT_NATIVE ||
      !initFormat;

    return {
      label: t('Device'),
      isLoading: true,
      elements: [
        {
          mainOption: {
            key: TargetingElementKeys.DEVICE_DESKTOP,
            label: t('Desktop'),
            icon: <Computer />,
            isSelected: isSelected(TargetingElementKeys.DEVICE_DESKTOP),
            isDisabled: isGenericDeviceDisabled,
            onClick: (_event, sectionKey, elementIndex) => {
              toggleElementSelection(sectionKey, elementIndex);
            },
          },
        },
        {
          mainOption: {
            key: TargetingElementKeys.DEVICE_MOBILE,
            label: t('Mobile'),
            icon: <MobileIcon />,
            isSelected:
              isSelected(TargetingElementKeys.DEVICE_MOBILE_SMARTPHONE) ||
              isSelected(TargetingElementKeys.DEVICE_MOBILE_TABLET),
            isDisabled: isGenericDeviceDisabled,
            onClick: (_event, sectionKey, elementIndex) => {
              toggleElementSelection(sectionKey, elementIndex);
            },
          },
          subOptions: [
            {
              key: TargetingElementKeys.DEVICE_MOBILE_SMARTPHONE,
              label: t('Smart Phone'),
              isSelected: isSelected(TargetingElementKeys.DEVICE_MOBILE_SMARTPHONE),
              isDisabled: isGenericDeviceDisabled,
              onClick: (_event, sectionKey, elementIndex, subElementIndex) => {
                toggleSubElementSelection(sectionKey, elementIndex, subElementIndex);
              },
            },
            {
              key: TargetingElementKeys.DEVICE_MOBILE_TABLET,
              label: t('Tablet'),
              isSelected: isSelected(TargetingElementKeys.DEVICE_MOBILE_TABLET),
              isDisabled: isGenericDeviceDisabled,
              onClick: (_event, sectionKey, elementIndex, subElementIndex) => {
                toggleSubElementSelection(sectionKey, elementIndex, subElementIndex);
              },
            },
          ],
        },
        {
          mainOption: {
            key: TargetingElementKeys.DEVICE_TV,
            label: t('TV Devices'),
            icon: <TVDevicesIcon />,
            isSelected:
              isSelected(TargetingElementKeys.DEVICE_TV_CONNECTEDTV) ||
              isSelected(TargetingElementKeys.DEVICE_TV_SETTOPBOX),
            isDisabled: isTvDevicesDisabled,
            onClick: (_event, sectionKey, elementIndex) => {
              toggleElementSelection(sectionKey, elementIndex);
            },
          },
          subOptions: [
            {
              key: TargetingElementKeys.DEVICE_TV_CONNECTEDTV,
              label: t('Connected TV'),
              isSelected: isSelected(TargetingElementKeys.DEVICE_TV_CONNECTEDTV),
              isDisabled: isTvDevicesDisabled,
              onClick: (_event, sectionKey, elementIndex, subElementIndex) => {
                toggleSubElementSelection(sectionKey, elementIndex, subElementIndex);
              },
            },
            {
              key: TargetingElementKeys.DEVICE_TV_SETTOPBOX,
              label: t('Set Top Box'),
              isSelected: isSelected(TargetingElementKeys.DEVICE_TV_SETTOPBOX),
              isDisabled: isTvDevicesDisabled,
              onClick: (_event, sectionKey, elementIndex, subElementIndex) => {
                toggleSubElementSelection(sectionKey, elementIndex, subElementIndex);
              },
            },
          ],
        },
      ],
    };
  }, [t, toggleElementSelection, toggleSubElementSelection, getValues]);
};
